// Ligne d'une notification dans le panneau de la cloche (WKF-013 §7.1).
// Extrait de NotificationCenter.tsx pour tenir la limite de 300 lignes par
// fichier ; comportement et props inchangés (API interne au dossier layout).
import { useTranslation } from 'react-i18next'

import {
    CLOSED_STATUSES,
    relativeTime,
    SOURCE_ICON,
    SOURCE_PASTILLE,
} from '@/components/notifications/notification-config'
import { PriorityChip } from '@/components/notifications/PriorityChip'
import { QuickSnoozeMenu } from '@/components/notifications/QuickSnoozeMenu'
import { QuickTransferMenu } from '@/components/notifications/QuickTransferMenu'
import { Checkbox } from '@/components/ui/checkbox'
import type { Notification } from '@/hooks/useNotifications.types'
import { htmlToPlainPreview } from '@/lib/richText'
import { cn } from '@/lib/utils'

export interface WidgetRowProps {
    n: Notification
    entityId: string | null
    canAssign: boolean
    onOpen: (n: Notification) => void
    onTreat: (id: string) => void
    onReopen: (id: string) => void
    onPickDate: (n: Notification) => void
    onTransfer: (n: Notification) => void
}

export function WidgetRow({ n, entityId, canAssign, onOpen, onTreat, onReopen, onPickDate, onTransfer }: WidgetRowProps) {
    const { t, i18n } = useTranslation()
    const lang = i18n.language
    const Icon = SOURCE_ICON[n.source_type]
    const isUnread = n.status === 'UNREAD'
    const isClosed = CLOSED_STATUSES.has(n.status)
    const preview = htmlToPlainPreview(n.message)
    const linkedLabel =
        n.actions?.[0]?.label ??
        (n.linked_entity_type
            ? t(`notifications.linkedType.${n.linked_entity_type}`, n.linked_entity_type)
            : null)
    const dueText = n.due_date
        ? new Date(n.due_date).toLocaleDateString(lang, { day: 'numeric', month: 'short' })
        : relativeTime(n.created_at, lang)

    return (
        <div
            className={cn(
                'flex items-start gap-2 rounded-lg px-2 py-2 hover:bg-sidebar-accent/50',
                // `notif-unread` : en mode couleur = teal léger ; en MONOCHROME, un
                // fond gris plus marqué (le token sidebar-accent y est à 96 % de
                // clarté = invisible) — cf. override html.neutral dans index.css.
                isUnread && !isClosed && 'bg-sidebar-accent/40 notif-unread',
            )}
        >
            <Checkbox
                className="mt-1 shrink-0"
                checked={isClosed}
                onCheckedChange={() => (isClosed ? onReopen(n.id) : onTreat(n.id))}
                aria-label={
                    isClosed
                        ? t('notifications.actions.reopen', 'Rouvrir')
                        : t('notifications.actions.treat', 'Marquer comme terminé')
                }
            />
            <span
                className={cn(
                    'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                    SOURCE_PASTILLE[n.source_type],
                )}
            >
                <Icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onOpen(n)}>
                <p className={cn('truncate text-sm', isUnread && !isClosed && 'font-medium', isClosed && 'text-muted-foreground line-through')}>
                    {n.title}
                </p>
                {preview && (
                    <p className="mt-0.5 line-clamp-2 break-words text-xs text-muted-foreground">{preview}</p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    <PriorityChip priority={n.priority} />
                    <span className={cn(n.is_overdue && !isClosed && 'font-medium text-destructive')}>
                        {n.is_overdue && !isClosed ? t('notifications.overdue', 'En retard') + ' · ' : ''}
                        {dueText}
                    </span>
                    {linkedLabel && <span className="truncate">· {linkedLabel}</span>}
                </div>
            </button>
            {!isClosed && (
                <div className="flex shrink-0 items-center gap-0.5">
                    <QuickSnoozeMenu notification={n} entityId={entityId} onPickDate={onPickDate} align="end" />
                    {canAssign && (
                        <QuickTransferMenu
                            notification={n}
                            entityId={entityId}
                            onMore={onTransfer}
                            align="end"
                        />
                    )}
                </div>
            )}
        </div>
    )
}
